// Opening move: Feature flag wrapper for Lexical editor reboot
// This component toggles between the legacy complex editor and the new minimal baseline
// Controlled by VITE_LEXICAL_LEGACY_EDITOR environment variable

import { forwardRef, lazy, Suspense, useRef, useCallback, useImperativeHandle } from 'react'
import type { LexicalEditor } from 'lexical'
import { $getSelection, $isRangeSelection, $getRoot } from 'lexical'
import type { LexicalLyricsEditorRef } from './LexicalLyricsEditor'
import BaselineEditor, { $parseAndTagContent } from './lexical/Editor'
import { useAnalyzerHealth } from './lexical/hooks/useAnalyzerHealth'
import { useUsedWordTracking } from '../hooks/useUsedWordTracking'
import { useWordCollectionStore } from '../store/wordCollectionStore'
import type { SectionRhymeSchemes } from '../types/rhymeScheme'

// Big play: Use dynamic import for legacy editor to avoid bundling into baseline path
const RichTextLyricsEditorLegacy = lazy(() => import('./RichTextLyricsEditorLegacy'))

// Helper to safely check environment variable (works in Vite and Jest)
// Vite uses import.meta.env, but Jest uses process.env
function isLegacyEditorEnabled(): boolean {
  // Check process.env (works in Jest and also in Vite build output)
  if (typeof process !== 'undefined' && process.env) {
    const envValue = process.env.VITE_LEXICAL_LEGACY_EDITOR
    if (envValue === '1' || envValue === 'true') {
      return true
    }
  }

  // Check window global for E2E test override
  if (typeof window !== 'undefined') {
    const windowEnv = (window as unknown as Record<string, unknown>).VITE_LEXICAL_LEGACY_EDITOR
    if (windowEnv === '1' || windowEnv === 'true') {
      return true
    }
  }

  // Default to baseline editor
  return false
}

export interface RichTextLyricsEditorProps {
  value: string
  onChange: (value: string) => void
  onLexicalChange?: (lexicalJSON: string) => void // Epic 06: Lexical JSON callback for save pipeline
  onTextContentChange?: (textContent: string) => void // Text content change detection - Lexical best practice
  onSelectionChange?: () => void
  placeholder?: string
  className?: string
  rows?: number
  enableAutoSave?: boolean
  autoSaveDelay?: number
  // onAutoSave removed - dead code (Epic 06 cleanup)
  enableProsodyAnalysis?: boolean
  enableSyllableMarking?: boolean
  enableRhymeScheme?: boolean
  enableStressMarking?: boolean
  /** Bypass analyzer health check for stress features (useful for tests that inject data programmatically) */
  bypassAnalyzerHealthCheck?: boolean
  editable?: boolean
  onStressPatternChange?: () => void // Epic 07: Callback when stress patterns are modified
  /** Rhythmic stability value (0-1) for rhyme scheme rendering (0=loose, 1=tight) */
  rhythmicStability?: number
  /** Per-section-type rhyme scheme configuration (Scope 21) */
  sectionSchemes?: SectionRhymeSchemes
}

/**
 * RichTextLyricsEditor - Feature flag wrapper
 *
 * Main play: Route to either baseline editor (default) or legacy editor based on feature flag
 * The minimal baseline is now the default path for all new development
 *
 * Feature flag: VITE_LEXICAL_LEGACY_EDITOR
 * - Set to '1' or 'true' to enable legacy editor with custom nodes/plugins
 * - Defaults to baseline editor (new clean foundation)
 */
const RichTextLyricsEditor = forwardRef<LexicalLyricsEditorRef, RichTextLyricsEditorProps>(
  (props, ref) => {
    // Opening move: Store editor reference for baseline path
    const editorRef = useRef<LexicalEditor | null>(null)
    // Big play: Queue for onEditorReady callbacks registered before editor is populated
    const callbackQueueRef = useRef<Array<() => void>>([])
    // Guard ref to prevent onChange loops during setContent
    const isUpdatingFromProps = useRef(false)

    // Time to tackle the tricky bit: check feature flag
    // Use helper function that works in both Vite and Jest environments
    const useLegacyEditor = isLegacyEditorEnabled()

    // Epic 05 Scope 02 Fix 1: Check analyzer health to gate stress features
    const { healthy: analyzerHealthy, loading: analyzerLoading, error: analyzerError } = useAnalyzerHealth()

    // Only enable stress marking if prop is true AND (analyzer is healthy OR bypass requested)
    // bypassAnalyzerHealthCheck is useful for tests that inject stress data programmatically
    const effectiveEnableStress = props.enableStressMarking && (analyzerHealthy || props.bypassAnalyzerHealthCheck)

    // Scope 04: Used word tracking hook (baseline path only)
    const words = useWordCollectionStore(state => state.words)
    useUsedWordTracking(editorRef.current, words)

    // Capture editor ref from EditorRefPlugin callback and drain queued callbacks
    const handleEditorRef = useCallback((editor: LexicalEditor) => {
      editorRef.current = editor

      // Drain any queued onEditorReady callbacks - call once then unregister
      if (callbackQueueRef.current.length > 0) {
        callbackQueueRef.current.forEach(callback => {
          // FIX: Unregister after first call to prevent repeated triggers on every editor update
          // Without this, callbacks fire on EVERY update (clicks, typing, focus) causing spurious auto-saves
          const unregister = editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
              callback()
              unregister() // Only call once, matching the "editor already available" path
            })
          })
        })
        callbackQueueRef.current = []
      }
    }, [])

    // Big play: Wrap onChange to respect guard ref
    const handleChange = useCallback((content: string) => {
      // Time to tackle the tricky bit: Skip onChange if we're updating from setContent
      if (isUpdatingFromProps.current) {
        return
      }
      props.onChange(content)
    }, [props])

    // Epic 06: Lexical JSON change handler for save pipeline
    // Main play: pass through Lexical JSON to parent component
    const handleLexicalChange = useCallback((lexicalJSON: string) => {
      // Time to tackle the tricky bit: Skip if we're updating from setContent
      if (isUpdatingFromProps.current) {
        return
      }
      if (props.onLexicalChange) {
        props.onLexicalChange(lexicalJSON)
      }
    }, [props])

    // Text content change handler - Lexical best practice
    // Main play: forward text content changes to parent
    const handleTextContentChange = useCallback((textContent: string) => {
      // Skip if we're updating from setContent
      if (isUpdatingFromProps.current) {
        return
      }
      if (props.onTextContentChange) {
        props.onTextContentChange(textContent)
      }
    }, [props])

    // Big play: Expose methods via ref for baseline path (mirrors legacy implementation)
    useImperativeHandle(ref, () => ({
      getTextareaElement: () => null, // No textarea in baseline editor
      getWysiwygElement: () => (editorRef.current?.getRootElement() || null) as HTMLDivElement | null,
      insertTextAtCursor: (text: string) => {
        if (editorRef.current) {
          editorRef.current.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              selection.insertText(text)
            }
          }, { tag: 'insert-text-at-cursor' })
        }
      },
      getCurrentCursorPosition: () => {
        // Rich text position is complex, return 0 for now
        return 0
      },
      setCursorPosition: () => {
        // Rich text positioning requires different approach
        // TODO: Implement if needed
      },
      focus: () => {
        if (editorRef.current) {
          editorRef.current.focus()
        }
      },
      getSelectedText: () => {
        if (editorRef.current) {
          return editorRef.current.getEditorState().read(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              return selection.getTextContent()
            }
            return ''
          })
        }
        return ''
      },
      wrapSelectedText: () => {
        // TODO: Implement rich text wrapping if needed
      },
      isSourceMode: () => false, // Baseline editor has no source mode
      jumpToSection: (sectionName: string) => {
        // TODO: Implement section navigation for baseline
        console.log(`Baseline editor: jumpToSection called for "${sectionName}"`)
      },
      onEditorReady: (callback: () => void) => {
        if (!editorRef.current) {
          // Opening move: Editor not yet available, queue the callback
          callbackQueueRef.current.push(callback)
          // Return no-op cleanup function
          return () => {
            // Remove from queue if cleanup is called before editor is ready
            const index = callbackQueueRef.current.indexOf(callback)
            if (index > -1) {
              callbackQueueRef.current.splice(index, 1)
            }
          }
        }

        // Main play: Editor available, register listener immediately
        const unregister = editorRef.current.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            // Editor is ready when it has content
            callback()
            unregister() // Only call once
          })
        })

        return unregister
      },
      setContent: (lyrics: string, options?: { format?: 'plain' | 'lexicalJSON' }) => {
        // Opening move: Check if editor is available
        if (!editorRef.current) {
          console.warn('Baseline editor: setContent called before editor is ready')
          return
        }

        // Big play: Set guard to prevent onChange loops
        isUpdatingFromProps.current = true

        try {
          // Main play: Update editor state with new content
          editorRef.current.update(() => {
            const root = $getRoot()
            root.clear()

            // Time to tackle the tricky bit: Handle format detection
            const format = options?.format || 'plain'

            if (format === 'lexicalJSON') {
              try {
                // Attempt to parse as Lexical JSON
                const editorState = editorRef.current!.parseEditorState(lyrics)
                editorRef.current!.setEditorState(editorState)
                return
              } catch (error) {
                console.warn('Failed to parse as Lexical JSON, falling back to plain text:', error)
              }
            }

            // Victory lap: Default to plain text with section tagging
            $parseAndTagContent(lyrics)
          }, { tag: 'ref-set-content' })
        } finally {
          // Victory lap: Clear guard after update completes
          setTimeout(() => {
            isUpdatingFromProps.current = false
          }, 100)
        }
      },
    }), [])

    // Big play: route to appropriate editor implementation
    if (useLegacyEditor) {
      // Legacy editor - complex implementation with custom nodes/plugins
      // Loaded dynamically to avoid bundling into the new baseline path
      return (
        <Suspense fallback={<div>Loading editor...</div>}>
          <RichTextLyricsEditorLegacy {...props} ref={ref} />
        </Suspense>
      )
    }

    // Victory lap: default to baseline editor - minimal, clean, foundation for future epics
    // Now with full EditorRef support via shared plugin
    // Epic 05 Scope 02 Fix 1: Show warning banner if analyzer is unhealthy
    // Don't show warning when bypass is enabled (tests inject data programmatically)
    return (
      <>
        {!analyzerLoading && !analyzerHealthy && props.enableStressMarking && !props.bypassAnalyzerHealthCheck && (
          <div
            data-testid="analyzer-health-warning"
            style={{
              padding: '8px 12px',
              backgroundColor: '#fef3c7',
              borderLeft: '4px solid #f59e0b',
              color: '#92400e',
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            ⚠️ Stress analysis tools are currently unavailable
            {analyzerError && `: ${analyzerError}`}
          </div>
        )}
        <BaselineEditor
          initialContent={props.value}
          placeholder={props.placeholder}
          className={props.className}
          onChange={handleChange}
          onLexicalChange={handleLexicalChange}
          onTextContentChange={handleTextContentChange}
          editable={props.editable}
          onEditorRef={handleEditorRef}
          enableStressData={effectiveEnableStress}
          enableStressOverlay={effectiveEnableStress}
          onStressPatternChange={props.onStressPatternChange}
          rhythmicStability={props.rhythmicStability}
          sectionSchemes={props.sectionSchemes}
        />
      </>
    )
  }
)

RichTextLyricsEditor.displayName = 'RichTextLyricsEditor'

export type { LexicalLyricsEditorRef as RichTextLyricsEditorRef }
export default RichTextLyricsEditor
